import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import './UnifiedRender.css';
import { Block } from '../classes/Block';
import {
  VideoSettings,
  RenderFormat,
  VIDEO_FORMATS,
  isVideoFormat,
} from './VideoSettings';
import { t } from '@lingui/core/macro';
import { useProject } from '../contexts/ProjectContext';
import { getApiUrl } from '../utils/api';
import { authFetch } from '../utils/authFetch';
import { ApiBlock, ApiCardGroup, ApiPayload } from '../utils/apiTypes';
import { Select, MenuItem, SelectChangeEvent } from '@mui/material';
import { exportToFormat, EXPORT_CONFIGS, ExportFormat, ExportLayout } from '../utils/exporterUtils';
import { CardGroup } from '../utils/cardGroups';
import { trackEvent } from '../statsig';
import { toFontUrlMap } from '../utils/fontHelpers';
import { buildCardsApiPayload, transformBlockToApiFormat } from '../utils/renderPayload';

interface UnifiedRenderProps {
  columnDefs: any[];
  sheetName: string;
  blocks: Block[];
  isEnabled: boolean;
  videoSettings: VideoSettings;
  cardGroups?: CardGroup[];
}

type RenderSelection = RenderFormat | 'TIFF_TRANSPARENT';

function getFormatDisplayName(format: RenderFormat): string {
  const displayNames: Record<RenderFormat, string> = {
    'H264': 'H264',
    'H265': 'H265',
    'ProRes_422_Proxy': 'ProRes 422 Proxy',
    'ProRes_422_LT': 'ProRes 422 LT',
    'ProRes_422': 'ProRes 422',
    'ProRes_422_HQ': 'ProRes 422 HQ',
    'ProRes_4444': 'ProRes 4444 (Transparent)',
    'ProRes_4444_XQ': 'ProRes 4444 XQ (Transparent)',
    'PDF': 'PDF',
    'PNG': 'PNG',
    'TIFF': 'TIFF',
  };
  return displayNames[format] || format;
}

function getSelectionDisplayName(selection: RenderSelection): string {
  return selection === 'TIFF_TRANSPARENT'
    ? 'TIFF (Transparent)'
    : getFormatDisplayName(selection);
}

function getBackendFormat(selection: RenderSelection): RenderFormat {
  return selection === 'TIFF_TRANSPARENT' ? 'TIFF' : selection;
}

function isTransparentSelection(selection: RenderSelection): boolean {
  return selection === 'TIFF_TRANSPARENT'
    || selection === 'ProRes_4444'
    || selection === 'ProRes_4444_XQ';
}

const createVideoRender = async (data: ApiPayload, projectId: string): Promise<{ status: string; render: { version: number } }> => {
  const response = await authFetch(`${getApiUrl()}/new-render?projectId=${projectId}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data)
  });
  if (!response.ok) {
    throw new Error('Failed to create render');
  }
  const result = await response.json();
  return result;
};

const UnifiedRender: React.FC<UnifiedRenderProps> = ({
  columnDefs,
  blocks,
  isEnabled,
  videoSettings,
  cardGroups = [],
}) => {
  const { selectedProject } = useProject();
  const initialSelectedFormat: RenderFormat = videoSettings.mode === 'cards' && videoSettings.video_format === 'PNG'
    ? 'PDF'
    : videoSettings.video_format;
  const [selectedFormat, setSelectedFormat] = useState<RenderSelection>(initialSelectedFormat);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [renderError, setRenderError] = useState(false);
  const submissionProjectRef = useRef(selectedProject?.id);
  const submittingRef = useRef(false);
  submissionProjectRef.current = selectedProject?.id;
  useEffect(() => { setRenderError(false); }, [selectedProject?.id]);
  const [showToast, setShowToast] = useState<boolean>(false);
  const reduceMotion = useReducedMotion();
  const [renderVersion, setRenderVersion] = useState<number | null>(null);
  const [tiffSplitCards, setTiffSplitCards] = useState<boolean>(
    videoSettings.mode === 'cards' && initialSelectedFormat === 'TIFF'
  );
  const [pdfLayout, setPdfLayout] = useState<ExportLayout>('landscape');
  const [formatMenuOpen, setFormatMenuOpen] = useState<boolean>(false);
  const toastTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isCardsMode: boolean = videoSettings.mode === 'cards';
  const backendSelectedFormat: RenderFormat = getBackendFormat(selectedFormat);
  const effectiveSelectedFormat: RenderFormat = isCardsMode && backendSelectedFormat === 'PNG'
    ? 'PDF'
    : backendSelectedFormat;
  const selectedTransparent: boolean = isTransparentSelection(selectedFormat);

  const handleRender = async () => {
    if (!selectedProject || submittingRef.current) return;
    submittingRef.current = true;
    setRenderError(false);

    const visibleBlocks: Block[] = blocks.filter((block: Block) => !block.isHidden);

    trackEvent('render_requested', {
      project_id: selectedProject.id,
      project_name: selectedProject.name,
      output_format: effectiveSelectedFormat,
      video_mode: videoSettings.mode,
      block_count: visibleBlocks.length,
      card_group_count: cardGroups.length,
      tiff_split_cards: tiffSplitCards,
      pdf_layout: pdfLayout,
    });
    setIsLoading(true);
    try {
      if (isVideoFormat(effectiveSelectedFormat)) {
        const renderData: ApiBlock[] | ApiCardGroup[] = isCardsMode
          ? buildCardsApiPayload({
            blocks,
            cardGroups,
            columnDefs,
            propertiesMode: 'render',
          })
          : visibleBlocks.map((block: Block) => transformBlockToApiFormat(block, {
            columnDefs,
            propertiesMode: 'render',
          }));

        const payload: ApiPayload = {
          video_settings: {
            background_color: videoSettings.background_color,
            fps_num: videoSettings.fps_num,
            resolution: videoSettings.resolution,
            transparent: selectedTransparent,
            video_time: videoSettings.video_time,
            aspect_ratio: videoSettings.aspect_ratio,
            video_format: effectiveSelectedFormat,
            fonts: JSON.stringify(toFontUrlMap(videoSettings.fonts)),
            logos: JSON.stringify(videoSettings.logos),
            mode: videoSettings.mode,
            ...(isCardsMode ? { card_framing: videoSettings.card_framing } : {}),
          },
          data: renderData,
        };

        const response = await createVideoRender(payload, selectedProject.id);

        if (!response || !response.render || typeof response.render.version !== 'number') {
          throw new Error('Invalid response from server: missing version number');
        }

        const version: number = response.render.version;
        trackEvent('render_started', {
          project_id: selectedProject.id,
          output_format: effectiveSelectedFormat,
          render_version: version,
          video_mode: videoSettings.mode,
        });
        setRenderVersion(version);
        setShowToast(true);

        if (toastTimeoutRef.current) {
          clearTimeout(toastTimeoutRef.current);
        }

        toastTimeoutRef.current = setTimeout(() => {
          setShowToast(false);
          toastTimeoutRef.current = null;
        }, 5000);

      } else {
        // Image export - call /export-png endpoint with format parameter
        const formatLower = effectiveSelectedFormat.toLowerCase() as ExportFormat;
        const config = EXPORT_CONFIGS[formatLower];

        await exportToFormat({
          projectId: selectedProject.id,
          projectName: selectedProject.name ?? '',
          blocks,
          cardGroups,
          columnDefs,
          videoSettings,
          config,
          transparent: selectedTransparent,
          tiffSplitCards,
          layout: isCardsMode && effectiveSelectedFormat === 'PDF' ? pdfLayout : undefined,
        });
        trackEvent('export_completed', {
          project_id: selectedProject.id,
          output_format: effectiveSelectedFormat,
          video_mode: videoSettings.mode,
          tiff_split_cards: tiffSplitCards,
          pdf_layout: pdfLayout,
        });
      }
    } catch (error) {
      console.error('Error during render:', error);
      trackEvent('render_failed', {
        project_id: selectedProject.id,
        output_format: effectiveSelectedFormat,
        video_mode: videoSettings.mode,
      });
      if (submissionProjectRef.current === selectedProject.id) setRenderError(true);
    } finally {
      submittingRef.current = false;
      setIsLoading(false);
    }
  };

  const handleFormatChange = (e: SelectChangeEvent<string>) => {
    const nextFormat: RenderSelection = e.target.value as RenderSelection;
    if (isCardsMode && nextFormat === 'PNG') {
      setSelectedFormat('PDF');
      return;
    }
    if (isCardsMode && (nextFormat === 'TIFF' || nextFormat === 'TIFF_TRANSPARENT')) {
      setTiffSplitCards(true);
    }
    trackEvent('render_format_changed', {
      project_id: selectedProject?.id,
      output_format: getBackendFormat(nextFormat),
      video_mode: videoSettings.mode,
      transparent: isTransparentSelection(nextFormat),
    });
    setSelectedFormat(nextFormat);
  };

  const handleFormatMenuClose = (): void => {
    setFormatMenuOpen(false);
  };

  const handlePdfLayoutSelect = (layout: ExportLayout): void => {
    setPdfLayout(layout);
    trackEvent('render_format_changed', {
      project_id: selectedProject?.id,
      output_format: 'PDF',
      video_mode: videoSettings.mode,
      pdf_layout: layout,
    });
    setSelectedFormat('PDF');
    setFormatMenuOpen(false);
  };

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (toastTimeoutRef.current) {
        clearTimeout(toastTimeoutRef.current);
      }
    };
  }, []);

  const isVideo: boolean = isVideoFormat(effectiveSelectedFormat);
  const buttonText = isLoading
    ? (isVideo ? t`Rendering...` : t`Exporting...`)
    : t`Render`;

  return (
    <>
      <div className="unified-render" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <Select
          value={selectedFormat}
          onChange={handleFormatChange}
          open={formatMenuOpen}
          onOpen={() => {
            setFormatMenuOpen(true);
          }}
          onClose={handleFormatMenuClose}
          style={{ width: '240px', height: '36px', fontSize: '14px' }}
          sx={{ '.MuiSelect-icon': { color: '#007CB2' } }}
          renderValue={(value) => (
            isCardsMode && value === 'PDF'
              ? `PDF ${pdfLayout === 'landscape' ? 'Landscape' : 'Portrait'}`
              : getSelectionDisplayName(value as RenderSelection)
          )}
        >
          <MenuItem disabled style={{ opacity: 0.6, fontWeight: 600, fontSize: '12px', marginTop: '8px' }}>
            {t`Image Formats`}
          </MenuItem>
          {isCardsMode ? [
            <MenuItem key="pdf-landscape" value="PDF" onClick={() => handlePdfLayoutSelect('landscape')}>
              PDF Landscape
            </MenuItem>,
          ] : (
            <MenuItem key="pdf-portrait" value="PDF" onClick={() => handlePdfLayoutSelect('portrait')}>
              PDF Portrait
            </MenuItem>
          )}
          <MenuItem value="PNG" disabled={isCardsMode}>PNG</MenuItem>
          <MenuItem value="TIFF">TIFF</MenuItem>
          <MenuItem value="TIFF_TRANSPARENT">TIFF (Transparent)</MenuItem>

          {/* Video Formats */}
          <MenuItem disabled style={{ opacity: 0.6, fontWeight: 600, fontSize: '12px' }}>
            {t`Video Formats`}
          </MenuItem>
          {VIDEO_FORMATS.map((format) => (
            <MenuItem key={format} value={format}>
              {getFormatDisplayName(format)}
            </MenuItem>
          ))}
          
          {/* Divider */}
        </Select>

        <button
          onClick={handleRender}
          disabled={!isEnabled || isLoading}
          className="export-button"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            position: 'relative'
          }}
        >
          {isLoading && (
            <div
              className="loading-spinner"
              style={{
                width: '16px',
                height: '16px',
                border: '2px solid #f3f3f3',
                borderTop: '2px solid #3498db',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite'
              }}
            />
          )}
          {buttonText}
        </button>
      </div>

      {renderError && <p className="workspace-action-error" role="alert">{t`Could not start the render or export. Try again.`} <button type="button" disabled={isLoading || !isEnabled} onClick={() => { setRenderError(false); void handleRender(); }}>{t`Retry`}</button></p>}

      {createPortal(
        <div className="render-toast-region" role="status" aria-live="polite" aria-atomic="true">
          <AnimatePresence>
            {showToast && renderVersion !== null && (
              <motion.div
                key="render-started"
                className="render-started-toast"
                initial={{ opacity: 0, y: reduceMotion ? 0 : 12 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: reduceMotion ? 0 : 8 }}
                transition={{ duration: reduceMotion ? 0 : 0.18, ease: 'easeOut' }}
              >
                <span className="render-started-toast-icon" aria-hidden="true">
                  <CheckCircleOutlineRoundedIcon />
                </span>
                <div className="render-started-toast-content">
                  <p className="render-started-toast-title">{t`Render started`}</p>
                  <p className="render-started-toast-description">
                    {t`Version ${renderVersion} is processing in the background.`}
                  </p>
                </div>
                <button
                  type="button"
                  className="render-started-toast-dismiss"
                  aria-label={t`Close`}
                  onClick={() => {
                    setShowToast(false);
                    if (toastTimeoutRef.current) {
                      clearTimeout(toastTimeoutRef.current);
                      toastTimeoutRef.current = null;
                    }
                  }}
                >
                  <CloseRoundedIcon fontSize="small" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>,
        document.body,
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default UnifiedRender;
